import { typeContextCourantToContextAppType, useAppContextStore } from '@/features/auth/context-app'
import type { NiveauPermission, TypePermission } from '@/features/domaine/types/opco'
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useAuthentificationStore } from '../../auth'
import { AuthAktoEtats, Utilisateur, UtilisateurBase } from '../../auth/authentification/modeles'

export const useAppStateStore = defineStore('app-state', () => {
  const storeAuth = useAuthentificationStore()
  const { utilisateur, etat } = storeToRefs(storeAuth)
  const appContext = useAppContextStore()
  const { contextCourant, contextesAutorises } = storeToRefs(appContext)

  const estUtilisateurAuthentifie = computed<boolean>(() => {
    return utilisateur.value instanceof UtilisateurBase
  })

  const estAuthentificationEnCours = computed<boolean>(() => {
    return [AuthAktoEtats.NonAuthentifié, AuthAktoEtats.Authentifié].includes(etat.value) === false
  })

  const estUtilisateurInterne = computed<boolean>(() => {
    return utilisateur.value?.estInterne === true
  })

  const estUtilisateurImpersonnalise = computed<boolean>(() => {
    return utilisateur.value instanceof Utilisateur && utilisateur.value.estImpersonnalisé === true
  })

  function getPermissions(typeDossier: TypePermission): NiveauPermission {
    if (!utilisateur.value?.contextsApp || !contextCourant.value) return 'Aucun'

    const typeContextApp = typeContextCourantToContextAppType(contextCourant.value)
    const permissions = utilisateur.value.contextsApp[typeContextApp]!

    return (permissions ? permissions[typeDossier] : 'Aucun') ?? 'Aucun'
  }

  const estAdmin = computed<boolean>(() => {
    if (!utilisateur.value?.contextsApp) return false
    const typeContextApp = typeContextCourantToContextAppType(contextCourant.value)
    return utilisateur.value.contextsApp[typeContextApp]?.Administration === 'Ecriture'
  })

  /** Indique s'il a minimum le rôle Lecture */
  const autoriseLecture = (...type: TypePermission[]): boolean => {
    return type.some((item) => getPermissions(item) !== 'Aucun')
  }

  /** Indique s'il a le rôle Ecriture */
  const autoriseEcriture = (...type: TypePermission[]): boolean => {
    return type.some((item) => getPermissions(item) === 'Ecriture')
  }

  const connexionAsync = () => {
    return storeAuth.authentificationRequiseAsync('/', false)
  }

  const inscriptionAsync = () => {
    return storeAuth.authentificationRequiseAsync('/', true)
  }

  return {
    estAdmin,
    utilisateur,
    connexionAsync,
    inscriptionAsync,
    deconnexionAsync: storeAuth.deconnexionAsync,
    estUtilisateurAuthentifie,
    estAuthentificationEnCours,
    estUtilisateurInterne,
    estUtilisateurImpersonnalise,
    contextCourant,
    contextesAutorises,
    getPermissions,
    autoriseEcriture,
    autoriseLecture,
  }
})

export type AppStateStore = ReturnType<typeof useAppStateStore>
